/**
 * WizardNavigation
 *
 * Shared footer with Back / Continue buttons.
 * On the last step, Continue becomes "Import" (the approval action).
 * The appointments step shows a secondary "Import Without Appointments" option
 * on the left side to avoid accidental clicks. The new-patients step shows a
 * secondary "Skip — Update Matched Only" option in the same position.
 */

import { ArrowBack as ArrowBackIcon, ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { Box, Button, useTheme } from '@mui/material';

import type { WizardNavigationProps } from '../ImportReviewWizard.types';

export const WizardNavigation = ({
  onBack,
  onNext,
  onSkipAppointments,
  onSkipNewPatients,
  isFirstStep,
  isLastStep,
  isApproving = false,
  showSkipAppointments = false,
  showSkipNewPatients = false,
}: WizardNavigationProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pt: 2,
        mt: 2,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          disabled={isFirstStep || isApproving}
          sx={{ visibility: isFirstStep ? 'hidden' : 'visible' }}
        >
          Back
        </Button>

        {showSkipAppointments && onSkipAppointments && (
          <Button
            variant="text"
            color="inherit"
            onClick={onSkipAppointments}
            disabled={isApproving}
            sx={{ color: theme.palette.text.secondary }}
          >
            Import Without Appointments
          </Button>
        )}

        {showSkipNewPatients && onSkipNewPatients && (
          <Button
            variant="text"
            color="inherit"
            onClick={onSkipNewPatients}
            disabled={isApproving}
            sx={{ color: theme.palette.text.secondary }}
          >
            Skip — Update Matched Only
          </Button>
        )}
      </Box>

      <Button
        variant="contained"
        endIcon={!isLastStep ? <ArrowForwardIcon /> : undefined}
        onClick={onNext}
        disabled={isApproving}
      >
        {isApproving ? 'Importing...' : isLastStep ? 'Import' : 'Continue'}
      </Button>
    </Box>
  );
};
